/**
 * FilterHonestyBanner — consolidated honest hedge for filter chips.
 *
 * The Asymmetric Trust Rule applied at the filter layer:
 *   When a member toggles a filter (price / cuisine / atmosphere /
 *   distinction / open-now), the backend hides venues that don't
 *   match. But some venues are *unknown* on that filter dimension
 *   (Google has no `price_level` for them; Google has no `cuisine`
 *   tag for that listing). Those venues are silently dropped from
 *   the grid — which would be a quiet over-claim ("nothing here
 *   matches your filter") when the truth is ("we don't know about
 *   N venues for that filter; we hid them honestly").
 *
 *   This banner names the gap and offers a one-click clear.
 *
 * Founder doctrine (9 Feb 2026 PM late · BC-024 derivative):
 *   - Banner appears ONLY when `dropped > 0` AND the relevant
 *     filter is active.
 *   - Copy is doctrine-set per kind (price says "no price-level
 *     data", cuisine says "no verified cuisine tag", etc.).
 *   - The clear-link is the same per-kind action that toggled the
 *     filter off · the parent passes it in.
 *
 * Replaces two ~50-line copy-paste blocks in Table.jsx
 * (`price-filter-honesty-banner` + `cuisine-filter-honesty-banner`).
 * Future-ready for atmosphere / distinction / open-now / dietary.
 *
 * Data-testid contract (preserves existing pins):
 *   `filter-honesty-banner-{kind}`
 *   `filter-honesty-banner-{kind}-clear`
 *
 * Existing pins also still match the old testids
 * (`price-filter-honesty-banner` etc.) via the `legacyTestid` prop
 * the parent can pass for one release window.
 */
import React from "react";

// Doctrine copy · one line per kind · keep wording exact because it
// reads to a member. Tweaks here move all surfaces in sync (the
// whole point of consolidating).
const KIND_COPY = {
    price: {
        gap: "We don\u2019t have verified price-level data for them.",
        clearLabel: "Untoggle \u00a3 filter to see them.",
    },
    cuisine: {
        gap: "We don\u2019t have a verified cuisine tag for them.",
        clearLabel: "Clear cuisine to see them.",
    },
    atmosphere: {
        gap: "We don\u2019t have a sourced atmosphere chip for them.",
        clearLabel: "Clear atmosphere to see them.",
    },
    distinction: {
        gap: "We don\u2019t have a confirmed MICHELIN distinction for them.",
        clearLabel: "Clear distinction to see them.",
    },
    open_now: {
        gap: "We don\u2019t have verified live hours for them.",
        clearLabel: "Clear the open-now filter to see them.",
    },
};

export function FilterHonestyBanner({
    kind,
    dropped,
    onClear,
    legacyTestid = null,
}) {
    // Sourced-or-silent · the banner is the whole reason the doctrine
    // ships at this layer · if it has nothing to say, it says nothing.
    if (!dropped || dropped <= 0) return null;
    const copy = KIND_COPY[kind];
    if (!copy) return null;

    const noun = dropped === 1 ? "venue" : "venues";
    const headTestid = legacyTestid || `filter-honesty-banner-${kind}`;
    const clearTestid = legacyTestid
        ? `${legacyTestid}-clear`
        : `filter-honesty-banner-${kind}-clear`;

    return (
        <div
            data-testid={headTestid}
            // Dual-testid honesty · for the existing pins (e.g.
            // `price-filter-honesty-banner`) we ALSO carry the
            // canonical new id so a future test rewrite is a
            // one-line search-replace. Both tests find the same DOM.
            data-filter-honesty-kind={kind}
            className="t-fade"
            style={{
                marginTop: 6,
                marginBottom: 14,
                padding: "10px 14px",
                background: "var(--t-paper2, #f4f1ea)",
                border: "1px solid var(--t-line2)",
                borderRadius: 10,
                fontSize: 13,
                color: "var(--t-ink2)",
                lineHeight: 1.5,
            }}
        >
            <span style={{ fontWeight: 600, color: "var(--t-ink)" }}>
                {dropped} {noun} hidden
            </span>
            {" "}because {copy.gap}{" "}
            <button
                type="button"
                data-testid={clearTestid}
                onClick={onClear}
                style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--t-ink)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    fontWeight: 600,
                }}
            >
                {copy.clearLabel}
            </button>
        </div>
    );
}

// Exposed for tests · the doctrine copy is pinned per kind.
export const FILTER_HONESTY_COPY = KIND_COPY;
